import React, { useEffect, useState } from 'react';
import MainLayout from '@/components/Layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, CreditCard, Calendar, DollarSign } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const TopUpVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const reference = searchParams.get('ref');
  const [verified, setVerified] = useState<boolean | null>(null);

  useEffect(() => {
    document.title = 'Top-up Verification - Growth Energy';
    
    // Simulate verification check
    if (reference) {
      setTimeout(() => setVerified(true), 500);
    } else {
      setVerified(false);
    }
  }, [reference]);

  return (
    <MainLayout title="Top-up Verification">
      <div className="p-4 max-w-2xl mx-auto space-y-6">
        <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-2">
            <CreditCard className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h2 className="text-xl font-semibold text-foreground">Top-up Verification</h2>
          </div>
          <p className="text-muted-foreground">Verify your top-up transaction</p>
        </div>

        {verified === null ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        ) : verified ? (
          <Card className="border-green-200 dark:border-green-800">
            <CardHeader className="bg-green-50 dark:bg-green-950/50">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                <CardTitle className="text-green-900 dark:text-green-100">Top-up Verified</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Reference Number</p>
                    <p className="font-semibold text-foreground">{reference}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <Calendar className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Verified On</p>
                    <p className="font-semibold text-foreground">{new Date().toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Transaction Status</p>
                    <p className="font-semibold text-green-600 dark:text-green-400">Completed</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  This is a valid top-up transaction processed by Growth Energy. Your account has been credited accordingly.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-red-200 dark:border-red-800">
            <CardHeader className="bg-red-50 dark:bg-red-950/50">
              <div className="flex items-center space-x-3">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                <CardTitle className="text-red-900 dark:text-red-100">Verification Failed</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">
                Unable to verify this top-up transaction. The QR code may be invalid or the reference is missing.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Please contact Growth Energy support if you believe this is an error.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default TopUpVerification;
